<template>
  <section class="section">
    <div class="container">
      <div class="section-header reveal">
        <span class="eyebrow">Education & Experience</span>
        <h2 class="heading-lg">Empowering Creativity Through Code</h2>
        <p class="body-lg">{{ summary }}</p>
      </div>

      <!-- Education -->
      <div class="resume-block reveal">
        <div class="resume-block__head">
          <h3 class="resume-block__title">Education</h3>
          <div class="resume-block__line"></div>
        </div>
        <div class="resume-cards">
          <article
            v-for="(edu, i) in educationList"
            :key="i"
            class="dark-card resume-card"
            :class="{ 'resume-card--active': hoveredEdu === i }"
            @mouseenter="hoveredEdu = i"
            @mouseleave="hoveredEdu = null"
          >
            <h4 class="heading-md resume-card__role">{{ edu.degree }}</h4>
            <span class="resume-card__date">{{ edu.duration }}</span>
            <p class="body-sm resume-card__institution">{{ edu.institution }}</p>
            <p class="body-sm">{{ edu.description }}</p>
          </article>
        </div>
      </div>

      <!-- Experience -->
      <div class="resume-block reveal reveal-delay-1">
        <div class="resume-block__head">
          <h3 class="resume-block__title">Experience</h3>
          <div class="resume-block__line"></div>
        </div>
        <div class="resume-cards">
          <article
            v-for="(exp, i) in experienceList"
            :key="i"
            class="dark-card resume-card"
            :class="{ 'resume-card--active': hoveredExp === i }"
            @mouseenter="hoveredExp = i"
            @mouseleave="hoveredExp = null"
          >
            <h4 class="heading-md resume-card__role">{{ exp.role }}</h4>
            <span class="resume-card__date">{{ exp.duration }}</span>
            <p class="body-sm resume-card__institution">{{ exp.company }} · {{ exp.location }}</p>
            <ul class="resume-card__list">
              <li v-for="(item, j) in exp.description.slice(0, 3)" :key="j">{{ item }}</li>
            </ul>
          </article>
        </div>
      </div>

      <!-- Projects -->
      <div class="resume-block reveal reveal-delay-2">
        <div class="resume-block__head">
          <h3 class="resume-block__title">Enterprise Projects</h3>
          <div class="resume-block__line"></div>
        </div>
        <div class="resume-cards">
          <article
            v-for="(proj, i) in projectList"
            :key="i"
            class="dark-card resume-card"
            :class="{ 'resume-card--active': hoveredProj === i }"
            @mouseenter="hoveredProj = i"
            @mouseleave="hoveredProj = null"
          >
            <h4 class="heading-md resume-card__role">{{ proj.name }}</h4>
            <span class="resume-card__date">{{ proj.duration }}</span>
            <p class="body-sm">{{ proj.description }}</p>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from "vue";
import resumeJson from "@/JsonFiles/resume.json";
import { useScrollReveal } from "@/composables/useScrollReveal";

useScrollReveal();

const summary = resumeJson.resume.summary;
const educationList = resumeJson.resume.education;
const experienceList = resumeJson.resume.experience;
const projectList = resumeJson.resume.projects;

const hoveredEdu = ref(null);
const hoveredExp = ref(null);
const hoveredProj = ref(null);
</script>

<style scoped>
.resume-block {
  margin-bottom: 3.5rem;
}

.resume-block:last-child {
  margin-bottom: 0;
}

.resume-block__head {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.resume-block__title {
  font-size: 1.25rem;
  font-weight: 700;
  white-space: nowrap;
}

.resume-block__line {
  flex: 1;
  height: 1px;
  background: var(--color-border);
  position: relative;
}

.resume-block__line::after {
  content: "";
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-border);
}

.resume-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}

@media (min-width: 768px) {
  .resume-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

.resume-card {
  padding: 2rem;
  position: relative;
  overflow: hidden;
  transition: border-color var(--transition), box-shadow var(--transition);
}

.resume-card--active {
  border-color: var(--color-accent);
  box-shadow: inset 3px 0 0 var(--color-accent);
}

.resume-card__role {
  margin-bottom: 0.375rem;
}

.resume-card__date {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.resume-card__institution {
  color: var(--color-accent) !important;
  margin-bottom: 0.75rem !important;
}

.resume-card__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.resume-card__list li {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  padding: 0.375rem 0;
  padding-left: 1rem;
  position: relative;
}

.resume-card__list li::before {
  content: "·";
  position: absolute;
  left: 0;
  color: var(--color-accent);
}
</style>
